/**
 * useMilestone — поставляет Layout'у ближайший непоказанный рубеж организации.
 *
 * Два триггера проверки:
 *   1) монтирование Layout (вход в приложение) — ловим рубеж, который взял
 *      коллега-админ, пока нас не было;
 *   2) событие window 'fixty:milestone-check' — сразу после создания Работы,
 *      чтобы «сотая» праздновалась в тот же момент, а не при следующем входе.
 *
 * Демо-тенантам не показываем: празднование засеянных скриптом данных обесценивает
 * механику (бэкенд их тоже отсекает, здесь — чтобы не ходить в сеть зря).
 *
 * Отметка «увидел» ставится при закрытии: пока модал не закрыт, рубеж остаётся
 * pending — вылет вкладки не съест празднование.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';

export const MILESTONE_CHECK_EVENT = 'fixty:milestone-check';

/** Попросить приложение проверить рубежи (зовётся после создания Работы). */
export function requestMilestoneCheck() {
  window.dispatchEvent(new CustomEvent(MILESTONE_CHECK_EVENT));
}

export function useMilestone(user) {
  const [milestone, setMilestone] = useState(null);

  // Празднования — тем, кто ведёт Работы организации, а не только свои
  const eligible = !!user && !user.is_demo && user.scope !== 'own';

  const check = useCallback(async () => {
    if (!eligible) return;
    try {
      // ?milestone=100 — dev-превью для вёрстки: бэкенд отдаёт карточку, не трогая
      // БД, и только если в .env.dev включён MILESTONE_PREVIEW. На проде параметр
      // ничего не делает.
      const qs = new URLSearchParams(window.location.search);
      const preview = qs.get('milestone');
      const res = await api.getPendingMilestone(preview, qs.get('metric'));
      if (res?.milestone) setMilestone(res.milestone);
    } catch { /* празднование не должно шуметь ошибками */ }
  }, [eligible]);

  useEffect(() => { check(); }, [check]);

  useEffect(() => {
    if (!eligible) return;
    window.addEventListener(MILESTONE_CHECK_EVENT, check);
    return () => window.removeEventListener(MILESTONE_CHECK_EVENT, check);
  }, [eligible, check]);

  const dismiss = useCallback(async () => {
    const id = milestone?.id;
    setMilestone(null);
    if (id) { try { await api.markMilestoneSeen(id); } catch { /* повторим при следующем входе */ } }
  }, [milestone]);

  return { milestone, dismiss };
}
